import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap/datepicker/datepicker';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { ActivitiesByDateRangeDto } from '../activity-calendar-service/activities-by-date-range-dto';
import { ActivityCalendarService } from '../activity-calendar-service/activity-calendar.service';
import { ActivityFilter } from '../activity-calendar-service/activity-filter';
import { ActivityType } from '../activity-snippet/activity-type';
import { datepickerAnimations, fadeIn, growBounce } from './calendar-animations';
import { DateRange } from './date-range';

@Component({
  selector: 'lp2-activity-calendar',
  templateUrl: './activity-calendar.component.html',
  styleUrls: ['./activity-calendar.component.scss'],
  animations: [growBounce, fadeIn, datepickerAnimations],
})
export class ActivityCalendarComponent implements OnInit {
  @ViewChild('datePicker') datePicker: NgbDatepicker;
  activityFilters: ActivityFilter[] = [];
  selectedActivityFilter: ActivityFilter;
  activityHistory: ActivitiesByDateRangeDto;
  activityDates: Date[];

  today: NgbDateStruct;
  displayDate: NgbDateStruct;
  isCalendarView = true;
  selectedDate: Date;
  maxCalendarDate: NgbDateStruct;
  isDisplayingToday = true;

  pageSize = 4;
  currentPage = 1;

  isLoading = true;
  monthActivityLoaded = 0;
  totalHours = 0;

  liveClassEnabled: boolean;

  constructor(
    private readonly activityCalendarService: ActivityCalendarService,
    private readonly oeAppPerson: OeAppPersonService
  ) {}

  ngOnInit() {
    this.liveClassEnabled = this.oeAppPerson.liveClassEnabled;
    this.activityCalendarService.getActivityFilters().subscribe((response) => {
      if (!response || !response[0]) {
        return;
      }
      this.activityFilters.push({
        name: 'all-types',
        id: -1,
      });
      const filterOutUnusedFilter = response[0].searchCriteria.filter(
        (c) => !this.filtersToBeFiltered().includes(c.name)
      );
      this.activityFilters = this.activityFilters.concat(filterOutUnusedFilter);
      this.activityFilters = this.activityFilters.map((filter) => ({
        name: 'lp2.activityList.' + filter.name.toLowerCase().replace(' ', '-'),
        id: filter.id,
      }));
      const activityFiltersIds = this.activityFilters.filter((f) => f.id >= 0).map((f) => f.id);

      const todayDate = new Date();
      this.today = this.getDateStructFromDate(todayDate);
      this.displayDate = this.getDateStructFromDate(todayDate);
      const lastDayCurrentMonth = this.getMonthStartEndDates(todayDate).end;
      this.maxCalendarDate = this.getDateStructFromDate(lastDayCurrentMonth);

      this.selectedActivityFilter = this.activityFilters[0];
      this.getMonthlyHistory(this.displayDate, activityFiltersIds);
    });
  }

  private filtersToBeFiltered(): string[] {
    const filterThisActivities = [ActivityType.PLACEMENT_TEST.toString()];
    if (!this.liveClassEnabled) {
      filterThisActivities.push(ActivityType.LIVE_CLASS.toString());
      filterThisActivities.push(ActivityType.PRIVATE_CLASS.toString());
    }
    return filterThisActivities;
  }

  getDateStructFromDate(date: Date): NgbDateStruct {
    return <NgbDateStruct>{ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  getDateFromDateStruct(dateStruct: NgbDateStruct): Date {
    return new Date(dateStruct.year, dateStruct.month - 1, dateStruct.day);
  }

  getMonthlyHistory(date: NgbDateStruct, activities: number[]) {
    this.activityDates = [];
    const dateRange = this.getMonthStartEndDates(this.getDateFromDateStruct(date));
    this.activityCalendarService
      .getRangeActivityHistory(dateRange.start, dateRange.end, activities)
      .subscribe((rangeActivityHistoryResponse) => {
        this.totalHours = rangeActivityHistoryResponse.totalHours;
        this.activityHistory = rangeActivityHistoryResponse;
        this.activityHistory.activityDates.forEach((activityDate) => {
          const parsedDate = this.stringToDate(activityDate);
          this.activityDates.push(parsedDate);
        });
        this.isLoading = false;
        this.monthActivityLoaded = this.monthActivityLoaded + 1;
      });
  }

  getSelectedActivityFilterIds(selectedFilter: ActivityFilter) {
    return selectedFilter.id >= 0
      ? [selectedFilter.id]
      : this.activityFilters.filter((f) => f.id >= 0).map((f) => f.id);
  }

  getMonthStartEndDates(date: Date): DateRange {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return <DateRange>{
      start: firstDay,
      end: lastDay,
    };
  }

  navigateToDifferentDate(event: NgbDatepickerNavigateEvent) {
    if (
      !this.selectedActivityFilter ||
      (this.displayDate.month === event.next.month && this.displayDate.year === event.next.year)
    ) {
      return;
    }
    this.displayDate = <NgbDateStruct>{
      year: event.next.year,
      month: event.next.month,
      day: 1,
    };
    this.getMonthlyHistory(this.displayDate, this.getSelectedActivityFilterIds(this.selectedActivityFilter));

    this.isDisplayingToday = this.displayDate.month === this.today.month && this.displayDate.year === this.today.year;
  }

  filterActivities() {
    if (!this.displayDate) {
      return;
    }
    this.getMonthlyHistory(this.displayDate, this.getSelectedActivityFilterIds(this.selectedActivityFilter));
  }

  isToday(date: NgbDateStruct): boolean {
    if (!date) {
      return false;
    }
    const today = new Date();
    return this.isDateStructEqualToDate(date, today);
  }

  isEventDay(dateStruct: NgbDateStruct): boolean {
    if (!dateStruct || !this.activityHistory) {
      return false;
    }
    return this.activityDates.find((d) => this.isDateStructEqualToDate(dateStruct, d)) ? true : false;
  }

  displayDayDetail(date: NgbDateStruct) {
    if (!this.isEventDay(date)) {
      return;
    }
    this.selectedDate = new Date(date.year, date.month - 1, date.day);
    this.isCalendarView = false;
  }

  displayCalendar() {
    this.isCalendarView = true;
  }

  navigateToToday() {
    if (this.isDisplayingToday) {
      return;
    }
    this.datePicker.navigateTo(this.today);
  }

  private stringToDate(date: string): Date {
    // mmddyyyy
    const month = date.slice(0, 2);
    const day = date.slice(2, 4);
    const year = date.slice(4, 8);
    return new Date(+year, +month - 1, +day);
  }

  private isDateStructEqualToDate(dateStruct: NgbDateStruct, date: Date): boolean {
    return (
      dateStruct.day === date.getDate() &&
      dateStruct.month === date.getMonth() + 1 &&
      dateStruct.year === date.getFullYear()
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/activity-calendar/activity-calendar/activity-calendar.component.ts